import React from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";
import { SearchX, CloudOff } from "lucide-react";
import { Skeleton } from "../ui/Skeleton";
import { cn } from "../../utils/cn";
import { ticketLabel } from "./tickets";
import {
  searchResultConversation,
  searchResultKey,
} from "../../pages/helpdesk/inbox/inboxModel";

// Las filas muestran la antigüedad con fromNow: el plugin y el locale se
// registran acá (igual que en ConversationList) para que el módulo funcione
// fuera de HelpdeskPage, que antes era quien los registraba.
dayjs.extend(relativeTime);
dayjs.locale("es");

// ── Búsqueda dentro de los mensajes ─────────────────────────────────────────

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Recorta el mensaje alrededor de la primera coincidencia: un mensaje largo
// mostrado desde el principio suele no contener el término buscado.
function buildSnippet(content, query, radius = 48) {
  const text = String(content || "").replace(/\s+/g, " ").trim();
  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (index < 0) {
    return text.length > radius * 2 ? `${text.slice(0, radius * 2)}…` : text;
  }
  const start = Math.max(0, index - radius);
  const end = Math.min(text.length, index + query.length + radius);
  return `${start > 0 ? "…" : ""}${text.slice(start, end)}${
    end < text.length ? "…" : ""
  }`;
}

// Resaltado sin dangerouslySetInnerHTML: se parte el string por el término y se
// envuelve cada coincidencia en <mark>.
function HighlightedText({ text, query }) {
  const term = String(query || "").trim();
  const value = String(text || "");
  if (!term) return <>{value}</>;
  const parts = value.split(new RegExp(`(${escapeRegExp(term)})`, "ig"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === term.toLowerCase() ? (
          <mark
            key={`${part}-${i}`}
            className="bg-brand-50 text-brand-700 font-semibold rounded-sm px-0.5"
          >
            {part}
          </mark>
        ) : (
          <React.Fragment key={`${part}-${i}`}>{part}</React.Fragment>
        )
      )}
    </>
  );
}

function MessageResultSkeleton() {
  return (
    <div className="px-4 py-2 space-y-1.5">
      <Skeleton className="h-3 w-28" />
      <Skeleton className="h-3 w-3/4" />
    </div>
  );
}

// Sección "En los mensajes": resultados server-side, DEBAJO de los locales.
export function MessageSearchResults({ query, loading, results, error, onSelect }) {
  const { t } = useTranslation();

  return (
    <section
      aria-label={t("Resultados en los mensajes")}
      aria-busy={loading}
      className="shrink-0 max-h-[45%] overflow-y-auto border-t border-border bg-bg"
    >
      <div className="sticky top-0 z-10 px-4 pt-2 pb-1 bg-bg text-[11px] font-semibold uppercase tracking-wider text-fg-muted">
        {t("En los mensajes")}
      </div>

      {loading ? (
        <>
          <MessageResultSkeleton />
          <MessageResultSkeleton />
          <MessageResultSkeleton />
        </>
      ) : error ? (
        // El servidor falló (5xx / red). NO es "no hay coincidencias": decirlo
        // sería mentir sobre el contenido del inbox. Tampoco es un error del
        // usuario, así que se queda en fg-muted, sin rojo: los resultados
        // locales de arriba siguen siendo válidos.
        <div className="flex flex-col items-center text-center px-6 py-5">
          <CloudOff className="w-5 h-5 mb-1.5 text-fg-subtle" aria-hidden="true" />
          <p className="text-xs text-fg-muted">
            {t("La búsqueda en los mensajes no está disponible.")}
          </p>
          <p className="mt-0.5 text-[11px] text-fg-subtle">
            {t("Los resultados de la lista siguen funcionando.")}
          </p>
        </div>
      ) : results.length === 0 ? (
        <div className="flex flex-col items-center text-center px-6 py-5">
          <SearchX className="w-5 h-5 mb-1.5 text-fg-subtle" aria-hidden="true" />
          <p className="text-xs text-fg-muted">
            {t("Ningún mensaje contiene ese texto.")}
          </p>
          <p className="mt-0.5 text-[11px] text-fg-subtle">
            {t("Prueba con una palabra más corta o sin acentos.")}
          </p>
        </div>
      ) : (
        results.map((result, index) => {
          const conversation = searchResultConversation(result);
          const name =
            conversation.contact_name ||
            conversation.external_id ||
            t("Contacto sin nombre");
          return (
            <button
              key={searchResultKey(result, index)}
              type="button"
              onClick={() => onSelect(result)}
              className={cn(
                "w-full min-h-[44px] px-4 py-2 flex flex-col gap-0.5 text-left border-b border-border/40",
                "transition-colors hover:bg-bg-muted/50",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset",
              )}
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-[13px] font-semibold text-fg truncate" title={name}>
                  {name}
                </span>
                {result.created_at && (
                  <span className="shrink-0 text-[11px] text-fg-muted tabular-nums">
                    {dayjs(result.created_at).fromNow(true)}
                  </span>
                )}
              </div>
              <p className="text-xs text-fg-muted line-clamp-2">
                <HighlightedText
                  text={buildSnippet(result.content, query)}
                  query={query}
                />
              </p>
            </button>
          );
        })
      )}
    </section>
  );
}

// Fila del ticket consultado, ARRIBA de los resultados de mensajes: cuando la
// consulta es un ticket, la respuesta exacta manda sobre los parecidos.
export function TicketLookupResult({ number, loading, ticket, error, onSelect }) {
  const { t } = useTranslation();

  return (
    <section
      aria-label={t("Ticket")}
      aria-busy={loading}
      className="shrink-0 border-t border-border bg-bg"
    >
      <div className="px-4 pt-2 pb-1 bg-bg text-[11px] font-semibold uppercase tracking-wider text-fg-muted">
        {t("Ticket")}
      </div>

      {loading ? (
        <MessageResultSkeleton />
      ) : error === "notfound" ? (
        <div className="flex flex-col items-center text-center px-6 py-5">
          <SearchX className="w-5 h-5 mb-1.5 text-fg-subtle" aria-hidden="true" />
          <p className="text-xs text-fg-muted">
            {t("No existe el ticket #{{t}}").replace("{{t}}", number)}
          </p>
        </div>
      ) : error ? (
        // Mismo canal que el error de la búsqueda de mensajes: no es un error
        // del usuario, así que ni rojo ni alarma.
        <div className="flex flex-col items-center text-center px-6 py-5">
          <CloudOff className="w-5 h-5 mb-1.5 text-fg-subtle" aria-hidden="true" />
          <p className="text-xs text-fg-muted">
            {t("No se pudo buscar el ticket. Inténtalo de nuevo.")}
          </p>
        </div>
      ) : ticket ? (
        <button
          type="button"
          onClick={() => onSelect(ticket)}
          className={cn(
            "w-full min-h-[44px] px-4 py-2 flex flex-col gap-0.5 text-left border-b border-border/40",
            "transition-colors hover:bg-bg-muted/50",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-inset",
          )}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-[13px] font-semibold text-fg tabular-nums truncate">
              {ticketLabel(ticket.ticket_number ?? number, t)}
            </span>
            <span
              className={cn(
                "inline-flex items-center shrink-0 px-2 h-[18px] rounded-full text-[11px] font-medium",
                ticket.status === "closed"
                  ? "bg-bg-muted text-fg-muted"
                  : "bg-status-success-soft text-status-success",
              )}
            >
              {t(ticket.status === "closed" ? "Cerrado" : "Abierto")}
            </span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-fg-muted min-w-0">
            <span className="truncate">
              {ticket.contact_name || ticket.contact_phone || t("Contacto sin nombre")}
            </span>
            {ticket.opened_at && (
              <span className="shrink-0 tabular-nums">
                · {dayjs(ticket.opened_at).fromNow(true)}
              </span>
            )}
          </div>
        </button>
      ) : null}
    </section>
  );
}
